// Data Lab v2 · Orders — KPI band + status/supplier/tier facets + hover preview.
// Same v2 shape as Products: KpiBand → FacetChips → LabListTable.
import { useEffect, useMemo, useState } from "react";
import Layout from "../../../components/Layout";
import api from "../../../lib/api";
import LabListTable from "../LabListTable";
import { money, dateGB, Chip, asText } from "../_shared";
import { KpiBand, KpiTile, FacetChips, V2Banner, disableV2 } from "./_shared";
import { T2Panel } from "../../quotes/_t2";
import { ShoppingBag, Truck, PoundSterling, Factory, Crown } from "lucide-react";

const GBP0 = (n) => `£${(Number(n) || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

const COLUMNS = [
  { key: "id",       label: "Order #",       path: "BCOrder.id",                 sortable: true, w: "w-32",
    fmt: (v, row) => <span className="font-mono text-[#C8A46B]">{v || row._id || "—"}</span> },
  { key: "date",     label: "Created",       path: "BCOrder.date_created",       sortable: true, w: "w-40", fmt: dateGB, className: "text-slate-300 font-mono text-xs" },
  { key: "status",   label: "Status",        path: "BCOrder.status",             sortable: true, w: "w-40",
    fmt: (v) => {
      if (!v) return "—";
      const s = String(v).toLowerCase();
      const tone = s.includes("shipped") || s.includes("complete") ? "green"
        : s.includes("cancel") || s.includes("refund") ? "red"
        : s.includes("await") || s.includes("pending") ? "warn" : "slate";
      return <Chip tone={tone}>{v}</Chip>;
    } },
  { key: "first",    label: "First",         path: "BCOrder.billing_address.first_name", w: "w-24" },
  { key: "last",     label: "Last",          path: "BCOrder.billing_address.last_name",  w: "w-28" },
  { key: "phone",    label: "Phone",         path: "BCOrder.billing_address.phone",      w: "w-32", className: "font-mono text-slate-400 text-xs" },
  { key: "zip",      label: "Postcode",      path: "BCOrder.billing_address.zip",        w: "w-24", className: "font-mono text-slate-300" },
  { key: "vip",      label: "Tier",          path: "TMOrder.VIPLevel",           sortable: true, w: "w-24",
    fmt: (v) => v ? <Chip tone={String(v).toLowerCase() === "gold" ? "warn" : String(v).toLowerCase() === "platinum" ? "teal" : "slate"}>{v}</Chip> : "—" },
  { key: "supplier", label: "Supplier",      path: "TMOrder.SupplierName",       sortable: true, w: "w-40" },
  { key: "total",    label: "Total inc VAT", path: "BCOrder.total_inc_tax",      sortable: true, w: "w-28", fmt: money, className: "font-mono text-white font-bold" },
];

export default function LabOrdersV2() {
  const [insights, setInsights] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/lab/orders/insights");
        setInsights(data);
      } catch { setInsights(null); }
    })();
  }, []);

  // Chip filters get merged into the free-text search query (same trick as v2 Products).
  const searchAppend = useMemo(() => [...statuses, ...suppliers, ...tiers].join(" "), [statuses, suppliers, tiers]);
  const topStatus   = insights?.statuses?.[0]?.status || "—";
  const topSupplier = insights?.suppliers?.[0]?.name  || "—";
  const topTier     = insights?.tiers?.[0]?.tier      || "—";

  return (
    <Layout>
      <div data-testid="lab-orders-v2">
        <V2Banner onDisable={disableV2} />
        <div style={{ marginBottom: 14 }}>
          <div className="t2-page-eyebrow">
            <ShoppingBag className="w-3 h-3" /> Data Lab · Orders
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "var(--t2-white)", margin: "4px 0 3px" }}>Orders</h1>
          <p className="t2-mono" style={{ fontSize: 11.5, color: "var(--t2-grey3)" }}>
            BigCommerce orders enriched with the Talmia-1 supplier tag &amp; VIP tier.
          </p>
        </div>

        <KpiBand testid="orders-kpis">
          <KpiTile label="Orders"       value={insights?.total ?? "…"}            icon={ShoppingBag}    testid="kpi-orders-total" />
          <KpiTile label="Total value"  value={GBP0(insights?.total_value)}       icon={PoundSterling} tone="orange" testid="kpi-orders-value" />
          <KpiTile label="Avg order"    value={GBP0(insights?.avg_value)}         icon={PoundSterling} tone="brass"  testid="kpi-orders-avg" />
          <KpiTile label="Top status"   value={asText(topStatus)}                 icon={Truck}         tone="teal"   testid="kpi-orders-status" />
          <KpiTile label="Top supplier" value={asText(topSupplier)}               icon={Factory}       tone="brass"  testid="kpi-orders-supplier" />
          <KpiTile label="Top tier"     value={asText(topTier)}                   icon={Crown}         tone="warn"   testid="kpi-orders-tier" />
        </KpiBand>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
          <T2Panel icon={Truck} title="Filter by status" testid="orders-facet-status">
            <FacetChips
              testid="orders-facet-status-chips"
              options={(insights?.statuses || []).map((s) => ({ value: s.status, count: s.count }))}
              selected={statuses}
              onChange={setStatuses}
            />
          </T2Panel>
          <T2Panel icon={Factory} title="Filter by supplier" testid="orders-facet-supplier">
            <FacetChips
              testid="orders-facet-supplier-chips"
              options={(insights?.suppliers || []).map((s) => ({ value: s.name, count: s.count }))}
              selected={suppliers}
              onChange={setSuppliers}
            />
          </T2Panel>
          <T2Panel icon={Crown} title="Filter by tier" testid="orders-facet-tier">
            <FacetChips
              testid="orders-facet-tier-chips"
              options={(insights?.tiers || []).map((t) => ({ value: t.tier, count: t.count }))}
              selected={tiers}
              onChange={setTiers}
            />
          </T2Panel>
        </div>

        <LabListTable
          collection="orders"
          columns={COLUMNS}
          defaultSort="BCOrder.date_created"
          detailPath={(id) => `/lab/orders/${id}`}
          searchPlaceholder="Search order #, name, phone, postcode, status, supplier…"
          testidPrefix="lab-order"
          extraSearch={searchAppend}
        />
      </div>
    </Layout>
  );
}
